import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Api from "../../services/Api";
import {getLanguage, showLanguageString} from "../../lang/helpers";
import iconArrowRight from "url:shared-assets/icons/black/arrow-right.svg";
import iconCalendarX from "url:shared-assets/icons/primary/calendar-x.svg";
import iconPlus2 from "url:shared-assets/icons/white/plus.svg";
import Stepper from "../../components/Stepper";
import {useNavigate} from "react-router";
import CustomButton from "../../components/CustomButton";
import Loading from "../../components/Loading";
import {CONFIG} from "../../constants";


interface SurveyRequestInterface {
  id: string;
  created: string;
  deadline: string;
  completed: string;
  status: string;
  selfAssessments: number;
  otherAssessments: number;
  lastCompletedSurveyId: string;
  surveyMultipleRecipientId?: string;
  surveyMultipleResponded?: boolean;
}

interface SurveyInterface {
  id: string;
  name: string;
  canCreateRequest: boolean;
  canInvite: boolean;
  seenReport: number;
  pendingResponseId?: string;
  lastSurveyRequest?: SurveyRequestInterface;
  requestsTotal: number;
  requestsCompleted: number;
}

interface CustomerCategoryInterface {
  id: string;
  name: string;
  name_sv: string;
  description_en: string;
  description_sv: string;
  intro_self_assessment_en: string;
  intro_self_assessment_sv: string;
  intro_assessment_en: string;
  intro_assessment_sv: string;
  intro_create_vs_en: string;
  intro_create_vs_sv: string;
  surveys: Array<SurveyInterface>;
  readiness: boolean;
  drivers: boolean;
}

interface CustomerSurveyResponseInterface {
  id: string;
  name: string;
  created: string;
}

interface CustomerSurveyInterface {
  id: string;
  name: string;
  responses: Array<CustomerSurveyResponseInterface>;
}

const Assessments = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loaded, setLoaded] = useState(false);
  const [customerCategories, setCustomerCategories] = useState<Array<CustomerCategoryInterface>>([]);
  const [customerSurveys, setCustomerSurveys] = useState<Array<CustomerSurveyInterface>>([]);

  useEffect(() => {
    getData();
  }, []);


  const getData = () => {
    api.get("client/assessments").then(response => {
      setCustomerCategories(response.json.customerCategories);
      setCustomerSurveys(response.json.customerSurveys);
      setLoaded(true);
    })
  }

  const createFeedbackRequest = (surveyId: string) => {
    api.post("client/survey_request/create", {survey_id: surveyId}).then((response) => {
      if (response.json.status === "ok") {
        navigate("/feedback/survey/" + response.json.surveyRequestId);
      } else {
        alert("Error");
      }
    })
  }

  if (!loaded) {
    return <Loading />;
  }

  const renderCustomerCategorySurvey = (customerCategory: CustomerCategoryInterface,
                                        survey: SurveyInterface) => {
    let lastSurveyRequest = survey.lastSurveyRequest;
    let canRequestAgain = false;
    if (lastSurveyRequest && lastSurveyRequest.completed) {
       canRequestAgain = survey.canCreateRequest || CONFIG.SYSTEM !== "production"
    }

    return (
      <div key={"category_" + customerCategory.id + "_" + survey.id} className="mt-6">
        <div className="flex justify-between items-center">
          <div className="z-h3">
            {showLanguageString(lang, customerCategory.name_sv, customerCategory.name)}
            {
              customerCategory.surveys.length > 1 && <>: {survey.name}</>
            }
          </div>
          {
            (customerCategory.readiness || customerCategory.drivers) &&
              <div className="flex justify-end">
                  <div className="flex gap-x-4">
                    {
                      customerCategory.readiness &&
                      <div className="z-link" onClick={() => navigate("/assessments/readiness/" + customerCategory.id)}>
                          Readiness
                      </div>
                    }
                    {
                      customerCategory.drivers &&
                        <div className="z-link" onClick={() => navigate("/feedback/survey_drivers/" + customerCategory.id)}>
                            Drivers
                        </div>
                    }
                  </div>

              </div>
          }
          {
            (canRequestAgain) &&
            <CustomButton
              buttonType="transparentBase"
              size="sm"
              onClick={() => createFeedbackRequest(survey.id)}
              leftIcon={<img src={iconPlus2} alt="info"  className="w-4 h-4"/>}
              style={{ width: "unset" }}
            >
              {lang === "sv" ? "Ny mätning" : "New assessment"}
            </CustomButton>
          }
        </div>


        <div className="mt-2 text-gray-400 block md:flex">
          <p>{showLanguageString(lang, customerCategory.intro_assessment_sv, customerCategory.intro_assessment_en)}</p>
        </div>


        <div className="rounded-md bg-indigo-800 p-4 mt-2 border-indigo-700 border">
          <div className="flex items-end">
            <div className="z-title2">{t("assessments")}:&nbsp;</div>
            <div className="z-body2">
              {survey.requestsCompleted} {t("of")} {survey.requestsTotal} {t("completed").toLowerCase()}
            </div>
          </div>

          {renderSurveyRequest(survey, lastSurveyRequest)}
        </div>

      </div>
    )
  }

  const renderSurveyRequest = (survey?: SurveyInterface,
                               surveyRequest?: SurveyRequestInterface) => {
    if (survey) {

      if (surveyRequest) {
        if (surveyRequest.completed) {
          const buttonType = survey.seenReport ? "outlineWhiteBase" : "blueBase"
          return (
            <div className="flex justify-end mt-4">
              <CustomButton buttonType={buttonType} style={{width: "unset"}}
                            onClick={() => navigate("/dev-plan/survey/result/" + surveyRequest.id + "/summary")}>
                {lang === "sv" ? "Visa rapport" : "View report"}
              </CustomButton>
            </div>
          )
        } else {
          if (surveyRequest.surveyMultipleRecipientId) {
            return (
              <div className="mt-4">
                <div className="mt-4">
                  {
                    surveyRequest.surveyMultipleResponded ? (
                      <div>Waiting for feedback</div>
                    ) : (
                      <CustomButton buttonType="outlineWhiteBase" style={{width: "unset"}} className="mt-4"
                                    onClick={() => navigate("/feedback/survey_multi/" + surveyRequest.surveyMultipleRecipientId)}>
                        {lang === "sv" ? "Ge feedback" : "Give feedback"}
                      </CustomButton>
                    )
                  }

                </div>
                {
                  surveyRequest.lastCompletedSurveyId &&
                    <div className="flex justify-end">
                        <CustomButton buttonType="outlineWhiteBase" style={{width: "unset"}} className="mt-4"
                                      onClick={() => navigate("/dev-plan/survey/result/" + surveyRequest.lastCompletedSurveyId + "/summary")}>
                          {lang === "sv" ? "Visa senaste rapporten" : "View latest report"}
                        </CustomButton>
                    </div>
                }
              </div>
            )
          }
          let description1: string;
          let description2 = lang === "sv" ? "Be om feedback"
            : "Send a request to those around you";
          let description3 = lang === "sv" ? "Resultat och analys av din självskattning och feedback"
            : "Results and analysis from your self-assessment and feedback";

          let buttonText = "";
          let buttonType: "blueBase"|"outlineWhiteBase" = "blueBase";
          let navigateTo = "";

          let step = 0;
          let completedSteps: Array<number> = [];

          if (surveyRequest.selfAssessments > 0) {
            completedSteps.push(0);
            step++;
            description1 = lang === "sv" ? "Klar" : "Completed";
          } else {
            description1 = lang === "sv" ? "Slutför din självskattning" : "Complete your self-assessment to continue";
            buttonText = lang === "sv" ? "Fortsätt med självskattning" : "Continue with self-assessment";
            if (surveyRequest.surveyMultipleRecipientId) {
              navigateTo = "/feedback/survey_multi/" + surveyRequest.surveyMultipleRecipientId;
            } else {
              navigateTo = "/feedback/survey/" + surveyRequest.id;
            }
          }

          if (step > 0) {
            if (surveyRequest.otherAssessments > 0) {
              completedSteps.push(1);
              step++;
              description2 = lang === "sv" ? "Feedbackförfrågan skickad" : "Feedback request sent";
              description3 = lang === "sv" ? "Väntar på svar. Vi meddelar dig via e-post när den är klar, troligen senast "
                : "Waiting for feedback result. We'll email you when it is ready, no later than ";
              description3 += surveyRequest.deadline.substring(0, 10) + ".";
              if (surveyRequest.lastCompletedSurveyId.length > 0) {
                buttonText = lang === 'sv' ? "Visa senaste rapporten" : "View latest report";
                buttonType = "outlineWhiteBase";
                navigateTo = "/dev-plan/survey/result/" + surveyRequest.lastCompletedSurveyId + "/summary";
              }
            } else if (!surveyRequest.surveyMultipleRecipientId) {
              buttonText = t("request-feedback");
              navigateTo = "/dev-plan/survey_request_invite/" + surveyRequest.id;
            }
          }
          const stepsData = [
            {
              title: t("self-assessment"),
              description: description1,
              content: null,
            },
            {
              title: lang === "sv" ? "Feedback": "Feedback request",
              description: description2,
              content: null,
            },
            {
              title: lang === "sv" ? "Insiktsrapport" : "Insights report",
              description: description3,
              content: null,
            },
          ];


          return (
            <div className="mt-4 bg-indigo-800 rounded-sm">
              <Stepper
                steps={stepsData}
                direction="vertical"
                currentIndex={step}
                completedSteps={completedSteps}
              />

              {
                (buttonText.length > 0  && surveyRequest.status !== "failed") &&
                  <div className="flex justify-end">
                      <CustomButton buttonType={buttonType} style={{width: "unset"}}
                                    rightIcon={buttonType === "blueBase" ? <img src={iconArrowRight} alt="right"/> : null}
                                    onClick={() => navigate(navigateTo)}>
                        {buttonText}
                      </CustomButton>
                  </div>
              }

              {
                surveyRequest.status === "failed" &&
                <div className="rounded-md bg-indigo-800 p-4 mt-4 border border-primary-500">
                    <div className="flex items-center">
                        <img src={iconCalendarX} alt="cal" className="h-6 w-6"/>
                        <div className="ml-2 z-title2">{t("not-enough-responses")}</div>
                    </div>
                  <div className="z-body2 mt-2">
                    {
                      lang === "sv" ? "Din förfrågan fick inte tillräckligt med svar. Prova att ändra mottagare och skicka igen." :
                        "Your previous request didn’t get enough responses: try adjusting the recipient list and sending it again."
                    }
                  </div>
                  <CustomButton buttonType="blueBase" className="mt-4 ml-auto" style={{width: "unset"}}
                                rightIcon={<img src={iconArrowRight} alt="right"/>}
                        onClick={() => navigate("/dev-plan/survey_request_invite/" + surveyRequest.id)}>
                    {
                      lang === "sv" ? "Skicka om förfrågan" : "Re-send feedback request"
                    }
                  </CustomButton>
                </div>
              }
              {
                surveyRequest.lastCompletedSurveyId &&
                  <div className="flex justify-end">
                      <CustomButton buttonType="outlineWhiteBase" style={{width: "unset"}} className="mt-4"
                                    onClick={() => navigate("/dev-plan/survey/result/" + surveyRequest.lastCompletedSurveyId + "/summary")}>
                        {lang === "sv" ? "Visa senaste rapporten" : "View latest report"}
                      </CustomButton>
                  </div>
              }

            </div>
          )
        }
      } else {
        return (
          <CustomButton buttonType="blueBase" className="mt-4 ml-auto" style={{width: "unset"}}
                        rightIcon={<img src={iconArrowRight} alt="right"/>}
                        onClick={() => createFeedbackRequest(survey.id)}>
            {lang === "sv" ? "Starta självskattning" : "Start self-assessment"}
          </CustomButton>
        );
      }
    }

    return (
      <div className="mt-4">Error - missing survey</div>
    );
  }

  const renderCustomerSurvey = (customerSurvey: CustomerSurveyInterface) => {
    return (
      <div key={"survey_" + customerSurvey.id} className="mt-6">
        <div className="z-h3">{customerSurvey.name}</div>
        <div className="mt-2 bg-indigo-800 rounded-sm p-4">
          {
            customerSurvey.responses.length === 0 &&
              <div>
                  <div className="z-body2">You have not uploaded your {customerSurvey.name} report yet.</div>
                  <div className="flex justify-end">
                      <CustomButton buttonType="blueBase" className="mt-4" style={{width: "unset"}}
                                    rightIcon={<img src={iconArrowRight} alt="right"/>}
                                    onClick={() => navigate("/assessments/survey_upload/" + customerSurvey.id)}>
                          Upload your report
                      </CustomButton>
                  </div>
              </div>
          }
          {
            customerSurvey.responses.map(response => (
              <div key={response.id} className="flex justify-end">
                <CustomButton buttonType="outlineWhiteBase" className="mt-4" style={{width: "unset"}}
                              onClick={() => navigate("/assessments/customer_survey_report/" + customerSurvey.id)}>
                  {lang === "sv" ? "Visa senaste rapporten" : "View latest report"}
                </CustomButton>
              </div>
            ))
          }
        </div>
      </div>
    )
  }


  return (
    <div className="main-one-col small">
      <div className="z-h2 mt-6">{t("assessments")}</div>
      {
        customerCategories.map(customerCategory => (
            <div key={"cat_" + customerCategory.id}>
              {
                customerCategory.surveys.map((survey) => {
                  return renderCustomerCategorySurvey(customerCategory, survey);
                })
              }
            </div>
        ))
      }
      {
        customerSurveys.map(customerSurvey => renderCustomerSurvey(customerSurvey))
      }
      {
        customerCategories.length === 0 &&
          <div className="mt-6">
            <div className="z-title1">
              {lang === "sv" ? "Inga mätningar tillgängliga just nu" : "No assessments available right now"}
            </div>
            <div className="z-body1 mt-1">
              {lang === "sv" ?
                "Mätningar aktiveras av din organisation och kommer att visas här när de blir tillgängliga." :
                "Your organization controls when assessments are activated. They will appear here when available."
              }
            </div>
          </div>
      }
      <div className="mt-4">&nbsp;</div>
    </div>
  );
}

export default Assessments;